import { FieldError } from 'react-hook-form';
import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import { isDevNodeEnv } from '@rover/rsdk/src/modules/Env/node';
import { Logger } from '@rover/rsdk/src/modules/Logging';

import { FIELD_ERROR_MESSAGE_TO_TRANSLATION_MAP } from './fieldErrorTranslationMap';

/**
 * NOTE(Ryan)
 *
 * It is tricky to augment zod's error messages with i18n, so this pattern tries
 * to solve it by translating the error messages right before they hit the UI.
 *
 * The tricky part of this will be getting translations in place for every one
 * of zod's built in error messages...
 *
 * But the cool thing is once we have that then folks will only need to think
 * about adding translations for errors specific to their forms.
 *
 * WARNING, this function does not currently gracefully handle cases where the
 * FieldError.message contains a number that is variable (e.g. "String must
 * contain at least 2 character(s)"). It can be handled by adding a translation
 * for every number, but that is a lot of work. If we find ourselves struggling
 * with numbers in error messages, we might be able to solve it by replacing the
 * numbers with a pattern like {0} in the string, and then having some
 * code that replaces the {0} with the number from the FieldError.message.
 */

const translateFieldError = (fieldError: FieldError, i18n: I18n): string => {
  // if we have a translation for the error message, use it
  const translation = fieldError.message
    ? FIELD_ERROR_MESSAGE_TO_TRANSLATION_MAP[fieldError.message]
    : undefined;
  if (translation) {
    return i18n._(translation);
  }
  // report missing error translation
  Logger.warn(`FieldError with no translation. Message: ${fieldError.message}`, {
    fieldErrorType: fieldError.type,
    fieldErrorMessage: fieldError.message || '',
  });
  // fallback to untranslated message or a last ditch generic error message
  const fallbackMessage = fieldError.message || i18n._(msg`Error`);
  // in dev, append a warning about lack of translation
  return isDevNodeEnv() ? fallbackMessage.concat(` (MISSING TRANSLATION!!!)`) : fallbackMessage;
};

export default translateFieldError;
